import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import type { ProxyRate, ProxyRateInput, ProxyRatesResponse } from '../../lib/types.js';
import { fmtMoney } from '../../lib/format.js';
import {
  hiddenColumnsNote,
  visibleColumns,
  type HideableColumn,
} from '../../lib/visibleColumns.js';
import {
  Button,
  Card,
  EmptyState,
  Field,
  StatusPill,
  TextInput,
  Toolbar,
  useConfirm,
} from '../../components/ui/index.js';
import { ADMIN_NOTE, errorMessage, fmtWhen } from './common.js';

/**
 * Proxy rates (Configuration → Buy side → Proxy rates): the dated schedule
 * the launcher's bill is priced by (server costs/proxy-rates.ts). One entry
 * per biller, vendor and period, with the four prices a proxy contract can
 * carry; every mirrored day is priced by its own date and posted per vendor
 * as a ledger line, so a rate that changes from a date never touches earlier
 * days; a change re-prices its own period from stored bytes and is journaled
 * with the total before and after.
 */
const typedDay = (v: string): string | null =>
  /^\d{4}-\d{2}-\d{2}$/.test(v.trim()) ? v.trim() : null;

/** A price field: blank reads as 0 (not charged); otherwise a finite number ≥ 0, else invalid (null). */
function priceOf(v: string): number | null {
  const t = v.trim();
  if (t === '') return 0;
  if (!/^\d+(\.\d+)?$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

const fmtRate = (n: number, decimals: number): string =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: decimals > 2 ? 2 : decimals, maximumFractionDigits: decimals })}`;

export function ProxyRatesCard({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const schedule = useQuery({
    queryKey: ['proxy-rates'],
    queryFn: () => api.get<ProxyRatesResponse>('/api/proxy-rates'),
  });
  const [editing, setEditing] = useState<number | null>(null);
  const [billedBy, setBilledBy] = useState('');
  const [vendor, setVendor] = useState('');
  const [fromDay, setFromDay] = useState('');
  const [toDay, setToDay] = useState('');
  const [perLaunch, setPerLaunch] = useState('');
  const [perGb, setPerGb] = useState('');
  const [baseMonth, setBaseMonth] = useState('');
  const [includedGb, setIncludedGb] = useState('');
  const [note, setNote] = useState('');
  const [msg, setMsg] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  const perLaunchN = priceOf(perLaunch);
  const perGbN = priceOf(perGb);
  const baseN = priceOf(baseMonth);
  const includedN = priceOf(includedGb);
  const from = fromDay.trim() === '' ? null : typedDay(fromDay);
  const to = toDay.trim() === '' ? null : typedDay(toDay);
  const daysOk =
    (fromDay.trim() === '' || from != null) &&
    (toDay.trim() === '' || to != null) &&
    (from == null || to == null || to >= from);
  const pricesOk = perLaunchN != null && perGbN != null && baseN != null && includedN != null;
  // At least one price above 0 (an all-zero entry would bill a month as free); included GB go with a fee.
  const hasPrice = pricesOk && (perLaunchN > 0 || perGbN > 0 || baseN > 0);
  const includedOk = pricesOk && (includedN === 0 || baseN > 0);
  const canSave = billedBy.trim() !== '' && daysOk && hasPrice && includedOk;

  const reset = () => {
    setEditing(null);
    setBilledBy('');
    setVendor('');
    setFromDay('');
    setToDay('');
    setPerLaunch('');
    setPerGb('');
    setBaseMonth('');
    setIncludedGb('');
    setNote('');
  };
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['proxy-rates'] });
    void qc.invalidateQueries({ queryKey: ['settings'] });
    void qc.invalidateQueries({ queryKey: ['pl'] });
    void qc.invalidateQueries({ queryKey: ['profit'] });
    void qc.invalidateQueries({ queryKey: ['invoices'] });
  };
  const repriced = (r: { days: number; totalBefore: number; totalAfter: number }) =>
    r.days === 0
      ? 'no mirrored day to re-price yet'
      : `${r.days} mirrored day${r.days === 1 ? '' : 's'} re-priced (${fmtMoney(r.totalBefore)} → ${fmtMoney(r.totalAfter)})`;
  const save = useMutation({
    mutationFn: (body: ProxyRateInput & { id?: number }) =>
      api.post<{
        ok: boolean;
        rate: ProxyRate;
        reprice: { days: number; totalBefore: number; totalAfter: number };
      }>('/api/proxy-rates', body),
    onSuccess: (res) => {
      setMsg({
        tone: 'ok',
        text: `${editing == null ? 'Added' : 'Updated'} — ${repriced(res.reprice)}.`,
      });
      reset();
      invalidate();
    },
    onError: (e) => setMsg({ tone: 'error', text: errorMessage(e) }),
  });
  const remove = useMutation({
    mutationFn: (id: number) =>
      api.delete<{
        ok: boolean;
        reprice: { days: number; totalBefore: number; totalAfter: number };
      }>(`/api/proxy-rates/${id}`),
    onSuccess: (res) => {
      setMsg({ tone: 'ok', text: `Removed — ${repriced(res.reprice)}.` });
      invalidate();
    },
    onError: (e) => setMsg({ tone: 'error', text: errorMessage(e) }),
  });
  const submit = () => {
    if (!canSave || perLaunchN == null || perGbN == null || baseN == null || includedN == null)
      return;
    save.mutate({
      ...(editing != null ? { id: editing } : {}),
      billedBy: billedBy.trim(),
      vendor: vendor.trim().toLowerCase(),
      fromDay: from,
      toDay: to,
      perLaunchUsd: perLaunchN,
      perGbUsd: perGbN,
      baseMonthUsd: baseN,
      includedGb: includedN,
      note: note.trim() || null,
    });
  };
  const edit = (r: ProxyRate) => {
    setEditing(r.id);
    setBilledBy(r.billedBy);
    setVendor(r.vendor);
    setFromDay(r.fromDay ?? '');
    setToDay(r.toDay ?? '');
    setPerLaunch(r.perLaunchUsd ? String(r.perLaunchUsd) : '');
    setPerGb(r.perGbUsd ? String(r.perGbUsd) : '');
    setBaseMonth(r.baseMonthUsd ? String(r.baseMonthUsd) : '');
    setIncludedGb(r.includedGb ? String(r.includedGb) : '');
    setNote(r.note ?? '');
    setMsg(null);
  };
  const askRemove = async (r: ProxyRate) => {
    const ok = await confirm<true>({
      title: `Remove the ${r.vendor || 'default'} entry billed by ${r.billedBy}?`,
      body: (
        <p>
          The mirrored days of its period are re-priced under what remains of the schedule, and the
          change is journaled. A day left with no rate at all is refused, so nothing is ever priced
          at $0.
        </p>
      ),
      choices: [{ label: 'Remove', value: true, variant: 'danger' }],
    });
    if (ok) remove.mutate(r.id);
  };

  const rows = schedule.data?.rates ?? [];
  const vendorsSeen = schedule.data?.vendors ?? [];
  const billers = schedule.data?.billers ?? [];
  const journal = schedule.data?.journal ?? [];
  const cols: HideableColumn<ProxyRate>[] = [
    { key: 'Billed by', get: (r) => r.billedBy, fixed: true },
    { key: 'Vendor', get: (r) => r.vendor || 'default', fixed: true },
    { key: 'From', get: (r) => r.fromDay ?? 'start', fixed: true },
    { key: 'To', get: (r) => r.toDay ?? 'open', fixed: true },
    { key: 'Per launch', get: (r) => r.perLaunchUsd || null },
    { key: 'Per GB', get: (r) => r.perGbUsd || null },
    { key: 'Monthly fee', get: (r) => r.baseMonthUsd || null },
    { key: 'GB included', get: (r) => r.includedGb || null },
    { key: 'Note', get: (r) => r.note },
  ];
  const { shown, hidden } = visibleColumns(rows, cols);
  const cell = (r: ProxyRate, key: string): ReactNode => {
    switch (key) {
      case 'Billed by':
        return r.billedBy;
      case 'Vendor':
        return (
          r.vendor || (
            <span className="dim">default — every vendor without an entry of its own</span>
          )
        );
      case 'From':
        return r.fromDay ?? 'start';
      case 'To':
        return r.toDay ?? 'open';
      case 'Per launch':
        return r.perLaunchUsd ? fmtRate(r.perLaunchUsd, 8) : '—';
      case 'Per GB':
        return r.perGbUsd ? fmtRate(r.perGbUsd, 6) : '—';
      case 'Monthly fee':
        return r.baseMonthUsd ? fmtMoney(r.baseMonthUsd) : '—';
      case 'GB included':
        return r.includedGb ? `${r.includedGb.toLocaleString('en-US')} GB` : '—';
      case 'Note':
        return r.note ?? '—';
      default:
        return null;
    }
  };
  const left = (key: string) =>
    key === 'Billed by' || key === 'Vendor' || key === 'From' || key === 'To' || key === 'Note'
      ? 'l'
      : undefined;
  const actionWord: Record<string, string> = {
    seed: 'seeded',
    add: 'added',
    update: 'changed',
    delete: 'removed',
  };
  const entryWord = (e: Record<string, unknown> | null): string => {
    if (!e) return '';
    const v = typeof e.vendor === 'string' && e.vendor ? e.vendor : 'default';
    const parts = [
      typeof e.perLaunchUsd === 'number' && e.perLaunchUsd
        ? `${fmtRate(e.perLaunchUsd, 8)}/launch`
        : '',
      typeof e.perGbUsd === 'number' && e.perGbUsd ? `${fmtRate(e.perGbUsd, 6)}/GB` : '',
      typeof e.baseMonthUsd === 'number' && e.baseMonthUsd
        ? `${fmtMoney(e.baseMonthUsd)}/month`
        : '',
      typeof e.includedGb === 'number' && e.includedGb ? `${e.includedGb} GB included` : '',
    ].filter(Boolean);
    return `${typeof e.billedBy === 'string' ? e.billedBy : ''} · ${v} · ${parts.join(' · ')}`;
  };

  return (
    <Card
      title="Proxy rates"
      status={
        rows.length ? (
          <StatusPill tone="ok">{`${rows.length} entr${rows.length === 1 ? 'y' : 'ies'}`}</StatusPill>
        ) : (
          <StatusPill tone="warn">No rate — the sync will refuse to price</StatusPill>
        )
      }
      description="What the launcher's bill is priced at, by date. Each entry says who bills it (Undetect for what goes through them, or the provider when bought direct), which proxy vendor it prices (blank = the default for every vendor without an entry of its own), from and to (blank = from the start, open-ended), and the prices: per launch (the default entry's applies), per GB, a monthly fee charged per day pro-rata, and the GB per month that fee includes before the per-GB price starts. Every mirrored day is priced by its own date and posted per vendor as a ledger line, so a rate that changes from a date never touches earlier days; editing an entry re-prices its own period from the stored bytes, and each change is journaled with the total before and after. Two entries for one vendor never overlap. Proxy usage Undetect cannot see is entered as a fixed monthly cost on the card further down; the invoice check compares each biller's invoice with its own lines."
    >
      {schedule.isError ? (
        <p className="ui-field__error">
          Could not load the schedule: {errorMessage(schedule.error)}
        </p>
      ) : null}
      {rows.length ? (
        <>
          <div className="table-scroll">
            <table className="stat-table">
              <thead>
                <tr>
                  {shown.map((c) => (
                    <th key={c.key} className={left(c.key)}>
                      {c.key}
                    </th>
                  ))}
                  {isAdmin ? <th className="l" /> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    {shown.map((c) => (
                      <td key={c.key} className={left(c.key)}>
                        {cell(r, c.key)}
                      </td>
                    ))}
                    {isAdmin ? (
                      <td className="l">
                        <Toolbar>
                          <Button variant="ghost" size="sm" onClick={() => edit(r)}>
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={remove.isPending}
                            onClick={() => void askRemove(r)}
                          >
                            Delete
                          </Button>
                        </Toolbar>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hiddenColumnsNote(hidden) ? (
            <p className="table-note">{hiddenColumnsNote(hidden)}</p>
          ) : null}
        </>
      ) : schedule.isSuccess ? (
        <EmptyState>
          No entry on the schedule — a mirrored day with no rate in effect fails the sync, named,
          rather than pricing at $0.
        </EmptyState>
      ) : null}
      {vendorsSeen.length ? (
        <p className="table-note">
          Vendors in the last 30 days of the bill:{' '}
          {vendorsSeen
            .map(
              (v) =>
                `${v.vendor || 'unnamed'} ${v.gb.toLocaleString('en-US')} GB (${fmtMoney(v.usd)})`,
            )
            .join(' · ')}
          .
        </p>
      ) : null}
      {isAdmin ? (
        <>
          <div className="cfg-grid">
            <Field
              label="Billed by"
              hint="Undetect for what goes through them; the provider's name when bought direct"
            >
              {(aria) => (
                <TextInput
                  {...aria}
                  list="proxy-billers"
                  value={billedBy}
                  onChange={(e) => setBilledBy(e.target.value)}
                />
              )}
            </Field>
            <datalist id="proxy-billers">
              {[...new Set(['Undetect', ...billers])].map((b) => (
                <option key={b} value={b} />
              ))}
            </datalist>
            <Field
              label="Vendor"
              hint="as Undetect's breakdown names it; blank = the default for every vendor without an entry"
            >
              {(aria) => (
                <TextInput
                  {...aria}
                  list="proxy-vendors"
                  value={vendor}
                  onChange={(e) => setVendor(e.target.value)}
                />
              )}
            </Field>
            <datalist id="proxy-vendors">
              {vendorsSeen
                .filter((v) => v.vendor)
                .map((v) => (
                  <option key={v.vendor} value={v.vendor} />
                ))}
            </datalist>
            <Field label="From" hint="blank = from the start of history">
              {(aria) => (
                <TextInput
                  {...aria}
                  type="date"
                  value={fromDay}
                  onChange={(e) => setFromDay(e.target.value)}
                />
              )}
            </Field>
            <Field label="To" hint={daysOk ? 'blank = open-ended' : 'on or after From'}>
              {(aria) => (
                <TextInput
                  {...aria}
                  type="date"
                  value={toDay}
                  onChange={(e) => setToDay(e.target.value)}
                />
              )}
            </Field>
            <Field
              label="USD per launch"
              hint="the default entry's applies to every launch; blank = not charged"
            >
              {(aria) => (
                <TextInput
                  {...aria}
                  type="number"
                  min={0}
                  step="0.0001"
                  inputMode="decimal"
                  value={perLaunch}
                  onChange={(e) => setPerLaunch(e.target.value)}
                />
              )}
            </Field>
            <Field label="USD per GB" hint="10⁹ bytes, as invoiced; blank = not charged">
              {(aria) => (
                <TextInput
                  {...aria}
                  type="number"
                  min={0}
                  step="0.01"
                  inputMode="decimal"
                  value={perGb}
                  onChange={(e) => setPerGb(e.target.value)}
                />
              )}
            </Field>
            <Field label="Monthly fee, USD" hint="charged per day: fee ÷ days in that month">
              {(aria) => (
                <TextInput
                  {...aria}
                  type="number"
                  min={0}
                  step="0.01"
                  inputMode="decimal"
                  value={baseMonth}
                  onChange={(e) => setBaseMonth(e.target.value)}
                />
              )}
            </Field>
            <Field
              label="GB included per month"
              hint={
                includedOk
                  ? 'covered by the monthly fee before the per-GB price starts'
                  : 'goes with a monthly fee'
              }
            >
              {(aria) => (
                <TextInput
                  {...aria}
                  type="number"
                  min={0}
                  step="1"
                  inputMode="decimal"
                  value={includedGb}
                  onChange={(e) => setIncludedGb(e.target.value)}
                />
              )}
            </Field>
            <Field label="Note">
              {(aria) => (
                <TextInput {...aria} value={note} onChange={(e) => setNote(e.target.value)} />
              )}
            </Field>
          </div>
          {pricesOk && !hasPrice && billedBy.trim() !== '' ? (
            <p className="ui-field__hint">
              At least one price above 0 — an entry with every price at 0 would bill a month as
              free.
            </p>
          ) : null}
          <Toolbar className="cfg-actions">
            <Button size="sm" loading={save.isPending} disabled={!canSave} onClick={submit}>
              {editing == null ? 'Add entry' : 'Save changes'}
            </Button>
            {editing != null ? (
              <Button variant="ghost" size="sm" onClick={reset}>
                Cancel
              </Button>
            ) : null}
          </Toolbar>
        </>
      ) : (
        <EmptyState>{ADMIN_NOTE}</EmptyState>
      )}
      {msg ? (
        <p className={msg.tone === 'ok' ? 'ui-field__hint' : 'ui-field__error'}>{msg.text}</p>
      ) : null}
      {journal.length ? (
        <details className="m-clean">
          <summary>
            Journal — {journal.length} change{journal.length === 1 ? '' : 's'}
          </summary>
          <ul className="table-note">
            {journal.map((j) => (
              <li key={j.id}>
                {fmtWhen(j.at)} · {j.username ?? 'system'} {actionWord[j.action] ?? j.action}{' '}
                {entryWord(j.entry ?? j.previous)}
                {j.action === 'update' && j.previous
                  ? ` (was ${entryWord(j.previous)})`
                  : ''} ·{' '}
                {repriced({
                  days: j.daysRepriced,
                  totalBefore: j.totalBefore,
                  totalAfter: j.totalAfter,
                })}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {dialog}
    </Card>
  );
}
